import React, { useCallback, useRef } from 'react';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    getFileExtensionWithDot,
    MAX_ATTACHMENT_SIZE_DEFAULT,
} from 'flow/components/tickets/Input/InputActions';
import {
    FILE_SIZE_EXCEEDED,
    FILE_TYPE_NOT_ALLOWED,
} from 'flow/components/tickets/Input/Attachments/AttachmentUploadError';
import { List } from 'immutable';
import { ChannelValues } from 'flow/components/tickets/messageChannels';
import { replyChannelSupportsAttachments } from 'flow/components/tickets/Input/hooks/useExternalReplyChannel';
import { PickedMention } from 'flow/types/Tickets';
import { TypedMap } from 'flow/types/TypedMap';

export interface MessageContent {
    text: string;
    attachment?: { name: string; file: File; aspectRatio?: number };
    imageSrc?: string;
    emailSignature?: string;
    /**
     * Set when the author removes the signature from this reply, so the effect
     * that applies the default one does not immediately put it back.
     */
    emailSignatureDismissed?: boolean;
    mentions?: List<TypedMap<PickedMention>>;
}

interface Props {
    setMessageContentInternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    setMessageContentReplyExternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    allowInputInExternalReply: boolean;
    tabSelected: 'internal' | 'reply_external';
    externalChannel?: ChannelValues;
    userAllowedToSendAttachments?: boolean;
    allowedAttachmentsData: any;
    blockedMimeTypes?: List<string>;
}

const SIGNATURE_ALL_FILE_TYPES = '*/*';

const isFileTypeAllowed = (
    file: File,
    allowedAttachmentsData: any,
    blockedMimeTypes: List<string>,
) =>
    !blockedMimeTypes?.includes(file.type) &&
    !blockedMimeTypes?.includes(SIGNATURE_ALL_FILE_TYPES) &&
    (!allowedAttachmentsData ||
        allowedAttachmentsData.has(file.type) ||
        allowedAttachmentsData.has(SIGNATURE_ALL_FILE_TYPES)); //About the check for !allowedAttachmentsData: If no such data is present, if we for some reason have not received the restriction data from the backend, we let the user send all file types; backend will validate anyway.

const useAttachmentHandler = ({
    setMessageContentInternal,
    setMessageContentReplyExternal,
    tabSelected,
    allowInputInExternalReply,
    externalChannel,
    userAllowedToSendAttachments,
    allowedAttachmentsData,
    blockedMimeTypes,
}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { openModal } = useModal();

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (!userAllowedToSendAttachments) return;
        if (
            tabSelected === 'reply_external' &&
            (!allowInputInExternalReply ||
                !replyChannelSupportsAttachments(externalChannel))
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const file = event.dataTransfer.files[0];
        if (file) {
            handleAddAttachment(file);
        }
    };

    const handleAddAttachment = (file: File) => {
        if (!userAllowedToSendAttachments) return;
        if (!file) return;

        if (tabSelected === 'reply_external') {
            if (
                !isFileTypeAllowed(
                    file,
                    allowedAttachmentsData,
                    blockedMimeTypes,
                )
            ) {
                //At the time of writing we don't have any data for allowed attachments for internal notes,
                //hence checking only for external replies.
                openModal('ticketAttachmentNotAllowed', {
                    channel: externalChannel,
                    fileExtensionUserFriendly: getFileExtensionWithDot(
                        file.name,
                    ),
                    fileMimeType: file.type,
                    reason: FILE_TYPE_NOT_ALLOWED,
                });
                return;
            }
        }

        const maxFileSize =
            allowedAttachmentsData?.getIn([file.type, 'maxSizeBytes']) ||
            allowedAttachmentsData?.getIn([
                SIGNATURE_ALL_FILE_TYPES,
                'maxSizeBytes',
            ]) ||
            MAX_ATTACHMENT_SIZE_DEFAULT; // Default to 20 MB if not specified

        if (file.size > maxFileSize) {
            openModal('ticketAttachmentNotAllowed', {
                channel:
                    tabSelected === 'internal' ? 'internal' : externalChannel,
                fileExtensionUserFriendly: getFileExtensionWithDot(file.name),
                fileMimeType: file.type,
                reason: FILE_SIZE_EXCEEDED,
            });
            return;
        }

        const attachmentObject = { name: file.name, file };

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            const imageSrc =
                (file?.type?.startsWith('image') &&
                    reader.result?.toString()) ||
                null;

            if (tabSelected === 'reply_external') {
                setMessageContentReplyExternal((prev) => ({
                    ...prev,
                    attachment: attachmentObject,
                    imageSrc,
                }));
            } else {
                setMessageContentInternal((prev) => ({
                    ...prev,
                    attachment: attachmentObject,
                    imageSrc,
                }));
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
    };

    const handleRemoveAttachment = () => {
        if (tabSelected === 'reply_external') {
            setMessageContentReplyExternal((prev) => ({
                ...prev,
                attachment: null,
                imageSrc: null,
            }));
        } else {
            setMessageContentInternal((prev) => ({
                ...prev,
                attachment: null,
                imageSrc: null,
            }));
        }
    };

    const handlePasteImage = (e: React.ClipboardEvent<HTMLElement>) => {
        if (!userAllowedToSendAttachments) return;
        if (
            tabSelected === 'reply_external' &&
            (!allowInputInExternalReply ||
                !replyChannelSupportsAttachments(externalChannel))
        ) {
            return;
        }

        const items = e.clipboardData.items;

        let file = null;
        for (const item of Object.values(items)) {
            if (item.type.startsWith('image')) {
                file = item.getAsFile();
            }
        }

        if (file) {
            handleAddAttachment(file);
            e.preventDefault();
        }
    };

    const onAttachmentAspectRatioFound = useCallback(
        (aspectRatio: number, tabSelected: 'internal' | 'reply_external') => {
            const setMessageContent =
                tabSelected === 'internal'
                    ? setMessageContentInternal
                    : setMessageContentReplyExternal;

            setMessageContent((prev) => ({
                ...prev,
                attachment: {
                    ...prev.attachment,
                    aspectRatio,
                },
            }));
        },
        [setMessageContentInternal, setMessageContentReplyExternal],
    );

    return {
        fileInputRef,
        handleDrop,
        handleAddAttachment,
        handleRemoveAttachment,
        handlePasteImage,
        onAttachmentAspectRatioFound,
    };
};

export default useAttachmentHandler;
